import styled, { css } from 'styled-components';

export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

export const TitleSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

export const PageTitle = styled.h1`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  word-wrap: break-word;
  margin: 0;
`;

export const ContentSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 24px;
`;

export const FormGroup = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

export const LoadingState = styled.div`
  text-align: center;
  padding: 50px;
  width: 100%;
`;

export const Dropdown = styled.div`
  position: relative;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

export const SelectButton = styled.button<{ $hasError?: boolean }>`
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 12px;
  position: relative;
  border-radius: 12px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: rgba(0, 0, 0, 0);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  cursor: pointer;
  gap: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  }

  ${(props) =>
    props.$hasError &&
    css`
      border-color: var(--Accent-Foreground-Red, #e52222);
    `}
`;

export const SelectValue = styled.span`
  flex: 1;
  min-height: 24px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  padding: 0 4px;
`;

export const SelectIcon = styled.div`
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  border-radius: 12px;
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  margin-top: 4px;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
`;

export const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: white;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--Fill-Alternative, rgba(112, 115, 124, 0.05));
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.08));
  }
`;

export const TitleInput = styled.input<{ $hasError?: boolean }>`
  align-self: stretch;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: rgba(0, 0, 0, 0);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: var(--Label-Assistive, rgba(55, 56, 60, 0.28));
  }

  &:hover {
    border-color: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  }

  &:focus {
    border-color: var(--Primary-Normal, #448181);
    box-shadow: 0 0 0 3px rgba(68, 129, 129, 0.1);
  }

  ${(props) =>
    props.$hasError &&
    css`
      border-color: var(--Accent-Foreground-Red, #e52222);

      &:focus {
        border-color: var(--Accent-Foreground-Red, #e52222);
        box-shadow: 0 0 0 3px rgba(229, 34, 34, 0.1);
      }
    `}
`;

export const EditorContainer = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  min-height: 300px;
`;

export const FooterSection = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

export const CheckboxSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

export const OptionSection = styled.div`
  width: 335px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

export const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  background: transparent;
  cursor: pointer;
  appearance: none;
  position: relative;

  &:checked {
    background: var(--Primary-Normal, #448181);
    border-color: var(--Primary-Normal, #448181);
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 0;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

export const CheckboxText = styled.span`
  flex: 1;
  padding-top: 1px;
  padding-bottom: 1px;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

export const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

export const CancelButton = styled.button`
  padding: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(112, 115, 124, 0.05);
  }

  span {
    text-align: center;
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
    font-size: 16px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.09px;
    word-wrap: break-word;
  }
`;

export const SubmitButton = styled.button`
  width: 160px;
  padding: 12px 28px;
  background: var(--Primary-Normal, #448181);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: background-color 0.2s ease;
  color: white;
  font-family: 'Pretendard JP', sans-serif;
  line-height: 24px;
  letter-spacing: 0.09px;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: var(--Primary-Dark, #3a6f6f);
  }

  &:disabled {
    background: var(--Fill-Disable, rgba(112, 115, 124, 0.22));
    cursor: not-allowed;
    color: var(--Label-Disable, rgba(55, 56, 60, 0.28));
  }

  @media (max-width: 768px) {
    width: auto;
    min-width: 160px;
  }
`;

export const ErrorMessage = styled.span`
  color: var(--Accent-Foreground-Red, #e52222);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.3px;
  margin-top: 4px;
`;
